import { Model, Signal, View } from '@lernetz/ts-lib';

import { Slide } from './Slide';
import template from './Quiz.html';
import { Subscription } from '@lernetz/ts-lib/src/Signal';

interface QuizConfig {
    id: string;
    openActionLabel: string;
    slides: Slide[];
    title?: string;
}

export class Quiz extends Model {

    public readonly close: Signal<void>;

    public canEvaluate: boolean;
    public canNavigateToPreviousSlide: boolean;
    public canProceed: boolean;
    public proceedLabel: string;

    public currentSlide: Slide;
    public lastSlide: Slide;
    public slides: Slide[];

    private updatingNavigation: boolean;
    
    constructor( config: QuizConfig ) {
        super( {
            canNavigateToPreviousSlide: false,
            canProceed: false,
            proceedLabel: 'checkAnswer',
            slides: [],
            ...config,
        } );

        this.close = new Signal<void>();

        this.currentSlide = this.slides[ 0 ];
        this.currentSlide.unlocked = true;

        this.lastSlide = this.slides[ this.slides.length - 1 ];

        this.propertyChange.subscribe( () => this.updateState() );
        for( const slide of this.slides ) {
            slide.propertyChange.subscribe( () => this.updateState() );
        }

        this.updateState();
    }

    public navigateToNextSlideOrClose() {
        if( this.currentSlide !== this.lastSlide ) {
            this.navigateToSlide( this.getNextSlide() );
        }
        else {
            this.close.dispatch();
        }
    }

    public navigateToPreviousSlide() {
        this.navigateToSlide( this.getPreviousSlide() );
    }

    public navigateToSlide( slide: Slide ) {
        if( !slide?.unlocked ) return;
        this.currentSlide = slide;
    }

    public proceed() {
        if( !this.canProceed ) return;

        const nextSlide = this.getNextSlide();

        if( this.currentSlide.evaluated ) {
            if( nextSlide && nextSlide.unlocked ) {
                this.currentSlide = nextSlide;
            }
            else {
                this.close.dispatch();
            }
        }
        else if( this.currentSlide.hasInput ) {
            this.currentSlide.evaluated = true;
            if( nextSlide ) {
                nextSlide.unlocked = true;
            }
        }
    }

    protected getNextSlide() {
        return this.slides[ this.slides.indexOf( this.currentSlide ) + 1 ] ?? null;
    }

    protected getPreviousSlide() {
        return this.slides[ this.slides.indexOf( this.currentSlide ) - 1 ] ?? null;
    }

    protected updateState() {
        if( this.updatingNavigation ) return;
        this.updatingNavigation = true;
        try {
            this.canEvaluate = !this.currentSlide.evaluated && this.currentSlide.hasInput;
            this.updateProceed();
            this.canNavigateToPreviousSlide = this.getPreviousSlide()?.unlocked;
            this.slides = [ ...this.slides ];  // trigger re-render of dot navigation
        }
        finally
        {
            this.updatingNavigation = false;
        }
    }

    protected updateProceed() {
        this.canProceed = this.currentSlide.evaluated || this.currentSlide.hasInput;
        this.proceedLabel = !this.currentSlide.evaluated ? 'checkAnswer' : this.currentSlide === this.lastSlide ? 'close' : 'continueQuiz';
    }
}


export class QuizView extends View<Quiz> {

    private subscription: Subscription;

    constructor() {
        super({ template });
    }

    init() {
        this.subscription = this.data.propertyChange.filter( p => p.name === 'currentSlide' ).subscribe( () => this.scroll() );
    }

    deinit() {
        this.subscription?.unsubscribe();
    }

    private scroll() {
        if( this.data.currentSlide.evaluated ) {
            this.node.querySelector( '.content .feedback' )?.scrollIntoView( { behavior: 'smooth' } );
        }
        else {
            this.node.querySelector( '.content' )?.scrollTo( { top: 0, behavior: 'smooth' } );
        }
    }
}
